import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Kanban, FileText } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface Board {
  id: string;
  slug: string;
  name: string;
  postCount?: number;
  openCount?: number;
  underReviewCount?: number;
  plannedCount?: number;
  inProgressCount?: number;
  _count?: {
    posts: number;
  };
}

function SidebarSkeleton() {
  return (
    <div className="space-y-1">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="px-3 py-2 flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-6" />
        </div>
      ))}
    </div>
  );
}

export function BoardsSidebar() {
  const params = useParams();
  const currentBoardSlug = params.slug as string;

  const { data, isLoading } = useQuery({
    queryKey: ['boards'],
    queryFn: () => api.boards.list(),
  });

  const boards: Board[] = data?.boards || [];

  return (
    <aside className="w-[220px] shrink-0 hidden lg:block">
      <div className="sticky top-[52px] pt-6">
        <div className="px-3 mb-4">
          <h2 className="text-base font-bold text-foreground flex items-center gap-2">
            <Kanban className="h-4 w-4" />
            Boards
          </h2>
        </div>

        <nav className="space-y-1">
          {isLoading ? (
            <SidebarSkeleton />
          ) : boards.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">No boards yet</p>
          ) : (
            boards.map((board) => {
              const isActive = board.slug === currentBoardSlug;
              // Show active posts count (Open + Under Review + Planned + In Progress)
              const activeCount = (board.openCount || 0) +
                (board.underReviewCount || 0) +
                (board.plannedCount || 0) +
                (board.inProgressCount || 0);

              return (
                <Link
                  key={board.id}
                  to={`/boards/${board.slug}`}
                  className={cn(
                    'px-3 py-2 text-sm font-medium rounded-md flex items-center gap-2 transition-colors mx-2',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )}
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  <span className="truncate flex-1">{board.name}</span>
                  <span className={cn(
                    'text-xs tabular-nums',
                    isActive ? 'text-primary/70' : 'text-muted-foreground/70'
                  )}>
                    {activeCount}
                  </span>
                </Link>
              );
            })
          )}
        </nav>
      </div>
    </aside>
  );
}
